import axios from 'axios'
import {
  rebuildHtml,
  isHtmlString,
  extractHtmlInnerText
} from './helper.js'

const DEEPSEEK_API_BASE_URL = 'https://api.deepseek.com/chat/completions'

// Request timeout (ms) - 30 minutes
const REQUEST_TIMEOUT = 30 * 60 * 1000

/**
 * Get DeepSeek API key from localStorage.
 * @returns {String} The API key or empty string.
 */
export function getDeepseekApiKey() {
  return localStorage.getItem('DEEPSEEK_API_KEY') || ''
}

/**
 * Save DeepSeek API key to localStorage.
 * @param {String} key - The API key to save.
 */
export function setDeepseekApiKey(key) {
  localStorage.setItem('DEEPSEEK_API_KEY', key || '')
}

// ... Giữ nguyên các hàm isHtmlString, extractHtmlInnerText, rebuildHtml từ code cũ của bạn ...

/**
 * Translates a single item from Vietnamese to target locales using DeepSeek API.
 * Translates all translatable keys in a SINGLE API call.
 * For HTML string values, extracts inner text via regex before sending to API, then maps back into HTML template.
 *
 * @param {Object} item - The item to translate.
 * @param {Array} locales - Target locale codes (e.g., ['en', 'ko', 'zh']).
 * @param {Array} columns - The columns definitions.
 * @param {String} type - The entity type (e.g., 'artifact', 'place').
 * @returns {Promise<Object>} - The translated item.
 */
export async function translateItemDeepseek(item, locales, columns, type) {
  const API_KEY = getDeepseekApiKey()
  if (!API_KEY) {
    throw new Error('DeepSeek API Key chưa được cấu hình. Vui lòng nhập API Key ở bước đầu tiên.')
  }

  const targetLocales = locales.filter(l => l !== 'vi')
  if (targetLocales.length === 0) {
    return item
  }

  const languageColumns = columns.filter((c) => c.language && !(c.languageExcludeTypes || []).includes(type))
  const translatableKeys = languageColumns.map(c => c.key)

  // Deep-clone the item and initialize locale structures
  const translatedItem = JSON.parse(JSON.stringify(item))
  targetLocales.forEach(loc => {
    if (!translatedItem[loc]) {
      translatedItem[loc] = {}
    }
    translatableKeys.forEach(key => {
      if (translatedItem[loc][key] === undefined) {
        translatedItem[loc][key] = ''
      }
    })
  })

  // Prepare payload for all translatableKeys at once
  const payloadToTranslate = {}
  const htmlMetadata = {}

  translatableKeys.forEach(key => {
    const value = item[key]
    if (!value || typeof value !== 'string' || value.trim() === '') {
      return
    }

    if (isHtmlString(value)) {
      const { isHtml, template, texts } = extractHtmlInnerText(value)
      if (isHtml && texts.length > 0) {
        htmlMetadata[key] = { template, texts }
        payloadToTranslate[key] = texts.map(t => t.text)
      } else {
        payloadToTranslate[key] = value
      }
    } else {
      payloadToTranslate[key] = value
    }
  })

  if (Object.keys(payloadToTranslate).length === 0) {
    return item
  }

  const localeList = targetLocales.join(',')

  // Thêm instruction yêu cầu trả về chuẩn JSON
  const prompt = `Translate the following JSON object from Vietnamese to target languages: [${localeList}].
Return a JSON object where top-level keys are target language codes (${localeList}), and each value is an object containing the translated key-value pairs matching the EXACT same JSON key structure as the input object.

Input JSON:
${JSON.stringify(payloadToTranslate, null, 2)}`

  try {
    const response = await axios.post(
      DEEPSEEK_API_BASE_URL,
      {
        model: 'deepseek-chat',
        messages: [
          {
            role: 'system',
            content: 'You are a professional translator API. You must strictly return ONLY a valid JSON object without any markdown wrapping or explanation.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        response_format: { type: 'json_object' }, // Bắt buộc DeepSeek trả về JSON
        temperature: 0
      },
      {
        headers: {
          'Authorization': `Bearer ${API_KEY}`,
          'Content-Type': 'application/json'
        },
        timeout: REQUEST_TIMEOUT
      }
    )

    // Parse response theo chuẩn OpenAI / DeepSeek
    const responseText = response.data.choices[0].message.content
    const translationResult = JSON.parse(responseText)

    targetLocales.forEach(loc => {
      const locData = translationResult[loc] || {}

      translatableKeys.forEach(key => {
        if (locData[key] !== undefined) {
          if (htmlMetadata[key]) {
            const translatedChunks = Array.isArray(locData[key]) ? locData[key] : [locData[key]]
            const translatedTextsMap = {}
            const originalTexts = htmlMetadata[key].texts

            originalTexts.forEach((tObj, idx) => {
              const translatedCoreText = translatedChunks[idx] !== undefined ? translatedChunks[idx] : tObj.text
              translatedTextsMap[tObj.index] = tObj.leading + translatedCoreText + tObj.trailing
            })

            translatedItem[loc][key] = rebuildHtml(htmlMetadata[key].template, translatedTextsMap)
          } else {
            translatedItem[loc][key] = locData[key]
          }
        }
      })
    })
  } catch (error) {
    console.error(`Failed to translate item:`, error.message || error)
    translatedItem._hasError = true
    translatedItem._errorMessage = error.message || 'Lỗi gọi API DeepSeek'
  }

  // Map back to the original item structure with translations merged
  const result = { ...item }
  if (translatedItem._hasError) {
    result._hasError = true
    result._errorMessage = translatedItem._errorMessage
  }
  targetLocales.forEach(loc => {
    result[loc] = { ...(item[loc] || {}), ...translatedItem[loc] }
  })
  return result
}
